import { Component, HostListener } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { createSelector, Store } from "@ngrx/store";
import { map, withLatestFrom } from "rxjs";
import { FreyaCommonModule } from "src/app/freya-common/freya-common.module";
import { eventTypeInfoMap } from "src/app/global.constants";

import { jobToolFeature, selectJob, selectJobTimezone, selectCustomerName, selectCustomer, selectJobLoading } from "src/app/job/job-tool.reducer";
import { FreyaHelperService } from "src/app/services/freya-helper.service";
import { ResponsiveHelperService } from "src/app/services/responsive-helper.service";
import { TimezoneHelperService } from "src/app/services/timezone-helper.service";
import { SharedModule } from "src/app/shared/shared.module";

import { ColorSchemeService } from "../../../../color-scheme.service";
import { dayjs } from '../../../../core/dayjs/dayjs';
import { BookingScheduleComponent } from "../../../../schedules/booking-schedule/booking-schedule.component";
import { selectDepositOutstandingToBookMinor } from "../../../../schedules/booking-schedule/booking-schedule.selectors";
import { EventInfoLocationJobsV2 } from '../../../job-events-helpers';

import { EventItineraryComponent } from "./event-itinerary/event-itinerary.component";
import { BookEventDialogActions } from "./timeline-book-event.actions";
import { timelineBookEventFeature } from "./timeline-book-event.feature";
import { eventScheduleSelectors, selectEventToBook, selectShowDockTravelTimeCheckbox } from "./timeline-book-event.selectors";

export const selectAssetTypes = createSelector(
    selectEventToBook,
    (eventToBook): string[] => (eventToBook && eventTypeInfoMap[eventToBook.type].assetTypes) || [],
);

export const selectFormDisabled = createSelector(
    selectJob,
    selectJobLoading,
    timelineBookEventFeature.selectBookEventDialog,
    eventScheduleSelectors.selectEventMissingLocations,
    (job, jobLoading, { restrictionsDisabled }, missingLocations,
    ) => {

        if (!job || jobLoading) { return true; }

        if (missingLocations?.length && !restrictionsDisabled) {
            return true;
        }

        return false;
    },
);

const selectDateDisabled = createSelector(
    selectFormDisabled,
    selectCustomer,
    (formDisabled, customer) => {
        if (formDisabled) { return true; }
        if (!customer) { return true; }
        return false;
    },
);

@Component({
    selector: 'app-timeline-book-event',
    standalone: true,
    imports: [
        FreyaCommonModule,
        SharedModule,
        ReactiveFormsModule,
        BookingScheduleComponent,
        EventItineraryComponent,
    ],
    templateUrl: './timeline-book-event.component.html',
    styleUrl: './timeline-book-event.component.scss'
})
export class TimelineBookEventComponent {

    @HostListener('window:keydown.escape', ['$event'])
    onEscapeKey(event: KeyboardEvent) {
        event.preventDefault();
        this.store.dispatch(this.actions.escapeKeyPressed());
    }

    public viewModel$ = this.store.select(createSelector({
        job: selectJob,
        timezone: selectJobTimezone,
        jobLoading: selectJobLoading,
        eventToBook: selectEventToBook,
        eventBeingScheduled: jobToolFeature.selectEventsLoading,
        customerName: selectCustomerName,
        possibleTimes: eventScheduleSelectors.selectAvailableTimes,
        possibleAssets: eventScheduleSelectors.selectAvailableAssets,
        selectedStartDate: createSelector(
          timelineBookEventFeature.selectBookEventDialog,
          ({ date }) => {
            // date-only -> local Date at midnight (no timezone math)
            const d = dayjs(date, 'YYYY-MM-DD');
            return new Date(d.year(), d.month(), d.date());
          },
        ),
        selectedTime: createSelector(timelineBookEventFeature.selectBookEventDialog, ({ time }) => time),
        showDockTravelTimeCheckbox: selectShowDockTravelTimeCheckbox,
        selectedAssets: createSelector(timelineBookEventFeature.selectBookEventDialog, ({ assetIds }) => assetIds),
        missingLocations: eventScheduleSelectors.selectEventMissingLocations,
        assetTypeNames: createSelector(
            selectAssetTypes,
            (assetTypes) => {
                if (!assetTypes?.length) { return 'Assets'; }
                return assetTypes.join(', ');
            }
        ),
        restrictionsDisabled: createSelector(
          timelineBookEventFeature.selectBookEventDialog,
          ({ restrictionsDisabled }) => restrictionsDisabled,
        ),
        showDepositRequiredError: selectDepositOutstandingToBookMinor,
        dateDisabled: selectDateDisabled,
        timeDisabled: createSelector(
            selectDateDisabled,
            timelineBookEventFeature.selectBookEventDialog,
            jobToolFeature.selectAvailabilityLoading,
            eventScheduleSelectors.selectAvailableTimes,
            (dateDisabled, { date }, loadingFindTime, availableTimes) => {
                if (dateDisabled) { return true; }
                if (!date) { return true; }
                if (!availableTimes?.length) { return true; }
                if (loadingFindTime) { return true; }
                return false;
            }
        ),
        assetsDisabled: createSelector(
            selectDateDisabled,
            eventScheduleSelectors.selectAvailableAssets,
            timelineBookEventFeature.selectBookEventDialog,
            jobToolFeature.selectAvailabilityLoading,
            (dateDisabled, possibleAssets, { date: selectedDate }, loadingFindTime) => {
                if (dateDisabled) { return true; }
                if (!selectedDate) { return true; }
                if (!possibleAssets?.length) { return true; }
                if (loadingFindTime) { return true; }
                return false;
            }
        ),
        loadingFindTime: jobToolFeature.selectAvailabilityLoading,
        bookButtonDisabled: createSelector(
            selectFormDisabled,
            jobToolFeature.selectEventsLoading,
            timelineBookEventFeature.selectBookEventDialog,
            jobToolFeature.selectAvailabilityLoading,

            selectDepositOutstandingToBookMinor,
            (
              formDisabled,
              eventBeingScheduled,
              {
                  date,
                  time,
                  assetIds,
              },
              loadingFindTime,
              depositRequired,
            ) => {

                if (formDisabled) { return true; }
                if (eventBeingScheduled) { return true; }
                if (!date) { return true; }
                if (!time) { return true; }
                if (!assetIds?.length) { return true; }
                if (loadingFindTime) { return true; }

                if (depositRequired && depositRequired > 0) { return true; }

                return false;
            },
        ),
        scheduleMode: createSelector(
            timelineBookEventFeature.selectBookEventDialog,
            ({ scheduleMode }) => scheduleMode,
        ),
        alreadyScheduled: createSelector(
            selectEventToBook,
            (event) => event && !['required', 'pending'].includes(event.status),
        ),
        closeButtonDisabled: jobToolFeature.selectEventsLoading,
        closeButtonDisabledTooltip: createSelector(
            jobToolFeature.selectEventsLoading,
            (bookEventLoading) => bookEventLoading
                ? 'Please wait until the booking process is complete.'
                : 'Close the Booking Dialog'
        ),
        rescheduleEventMode: createSelector(
            timelineBookEventFeature.selectBookEventDialog,
            ({ rescheduleEventMode }) => rescheduleEventMode,
        ),
        bookEventDialogVisible: createSelector(
            timelineBookEventFeature.selectBookEventDialog,
            ({ eventId }) => Boolean(eventId),
        ),
        saveAsDraftButtonLoading: jobToolFeature.selectEventsLoading,
        eventItinerarySummary: eventScheduleSelectors.selectItinerarySummary,
    })).pipe(
        withLatestFrom(this.freyaHelperService.lockDate$),
        map(([ storeValues, lockDate ]) => {

            const emptyLockDate = 'No Lock Date';

            let longLockDate = emptyLockDate;
            let shortLockDate = emptyLockDate;

            if (lockDate) {
                longLockDate = this.timezoneHelper.getDayJsTimeZoneObject(lockDate).format('h:mm a, MMM DD');
                shortLockDate = this.timezoneHelper.getDayJsTimeZoneObject(lockDate).format('MM DD');
            }

            return {
                ...storeValues,
                formattedLockDate: {
                    long: longLockDate,
                    short: shortLockDate,
                }
            }
        }),
    );

    public actions = BookEventDialogActions;
    public timePlaceholder = 'Select Time';

    constructor(
        public store: Store,
        public freyaHelperService: FreyaHelperService,
        public responsiveHelper: ResponsiveHelperService,
        private timezoneHelper: TimezoneHelperService,
        public colorScheme: ColorSchemeService,
    ) { }

    trackByLocationName(index: number, location: EventInfoLocationJobsV2) {
        return location?.name || index;
    }

    public handleDateSelected(date: Date) {
      this.store.dispatch(this.actions.dateSelected({
        date: dayjs(date).format('YYYY-MM-DD'),
      }));
    }
}
